import { Component, computed, effect, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { forkJoin, throwError } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { ApiCredentialDto, ApiCredentialState, ApiSettingsDto } from '../../shared/models/api.model';
import { AuthUser, Role } from '../../shared/models/user.model';
import { UserApiService } from '../../shared/services/api.service';
import { ModalService } from '../../shared/services/modal.service';
import { NotificationService } from '../../shared/services/notification.service';
import { UsersService } from '../../shared/services/users.service';
import { ValidationService, Validators as Vld } from '../../shared/services/validation.service';

@Component({
  standalone: false,
  selector: 'app-api-settings',
  templateUrl: './api-settings.component.html',
  styleUrls: ['./api-settings.component.scss'],
})
export class ApiSettingsComponent implements OnInit {
  @ViewChild('regenerateModal', { read: TemplateRef, static: false }) regenerateModal: TemplateRef<unknown>;
  @ViewChild('deleteCredentialModal', { read: TemplateRef, static: false }) deleteCredentialModal: TemplateRef<unknown>;

  readonly allowedIps = signal<string[]>([]);
  readonly currentUser = signal<AuthUser | null>(null);
  readonly loading = signal<boolean>(false);

  readonly apiSettings = signal<ApiSettingsDto | null>(null);
  readonly settingsLoading = signal<boolean>(false);
  readonly savingSettings = signal<boolean>(false);
  readonly settingsEnabled = signal<boolean>(false);

  readonly apiCredentials = signal<ApiCredentialDto[]>([]);
  readonly isCredentialCreating = signal<boolean>(false);
  readonly credentialsState = signal<Record<number, ApiCredentialState>>({});

  readonly isMain = computed(() => this.currentUser()?.role === Role.MAIN || this.currentUser()?.role === Role.DEPUTY);
  readonly hasSettings = computed(() => !!this.apiSettings());
  readonly shouldPopulateSettings = computed(() => !this.hasSettings() && !this.settingsLoading());
  readonly canCreateCredential = computed(() => {
    return this.settingsEnabled() && !this.isCredentialCreating() && this.apiCredentials().length < 5;
  });
  readonly canDeleteCredential = computed(() => this.apiCredentials().length > 1);

  form: FormGroup;

  callbackUrlLiveTesting = '';
  generalCallbackUrlLiveTesting = '';
  formLnt: FormGroup;

  constructor(
    public service: UserApiService,
    public userService: UsersService,
    public router: Router,
    public notificationService: NotificationService,
    titleService: Title,
    formBuilder: FormBuilder,
    public validationService: ValidationService,
    public modal: ModalService
  ) {
    titleService.setTitle('API settings');
    this.form = formBuilder.group({
      enabled: [''],
      allowedIps: ['', Vld.maxLength(500)],
      ip: ['', Vld.compose([Vld.ip(true)])],
    });
    this.formLnt = formBuilder.group({
      callbackUrlLiveTesting: ['', Vld.compose([Vld.url(true)])],
      generalCallbackUrlLiveTesting: ['', Vld.compose([Vld.url(true)])],
    });

    effect(() => {
      if (this.settingsLoading()) {
        this.form.controls.enabled.disable();
      } else {
        this.form.controls.enabled.enable();
      }
    });
  }

  ngOnInit() {
    this.userService.getAuthUser().then(i => {
      this.currentUser.set(i);
      this.init(i);
    });
  }

  init(user: AuthUser) {
    const isMain = user.role === Role.MAIN || user.role === Role.DEPUTY;

    this.userService.getCallbackUrlLiveTesting().subscribe(callbackUrlLiveTesting => {
      this.callbackUrlLiveTesting = callbackUrlLiveTesting ? callbackUrlLiveTesting : '';
      if (callbackUrlLiveTesting) {
        this.formLnt.controls.callbackUrlLiveTesting.setValue(callbackUrlLiveTesting);
      }
    });
    if (isMain) {
      this.userService.getGeneralCallbackUrlLiveTesting().subscribe(url => {
        this.generalCallbackUrlLiveTesting = url ? url : '';
        if (url) {
          this.formLnt.controls.generalCallbackUrlLiveTesting.setValue(url);
        }
      });
    }

    this.settingsLoading.set(true);

    this.service.getSettings(user.id).subscribe({
      next: settings => {
        this.apiSettings.set(settings);
        this.allowedIps.set(this.parseAllowedIps(settings.allowedIps));
        this.settingsEnabled.set(settings.enabled);
        this.loadCredentials();
      },
      error: e => {
        this.settingsLoading.set(false);

        if (e.status === 404) {
          return;
        }

        this.notificationService.error({
          title: 'Api settings',
          message: 'Receive settings an error',
          serviceName: 'GATEWAY',
          requestMessage: e.statusText,
          requestCode: e.status,
        });
      },
    });
  }

  populateInitialSettingsAndCredentials(): void {
    const user = this.currentUser();
    if (!user) return;

    const appKey = this.randomString(35);
    const payload: ApiSettingsDto = {
      enabled: true,
      allowedIps: null,
      userId: user.id,
      updatedAt: null,
    };

    this.service
      .createApiSettings(payload)
      .pipe(
        switchMap(settings => {
          this.apiSettings.set(settings);
          this.allowedIps.set(this.parseAllowedIps(settings.allowedIps));
          return this.service.createCredential({ appKey });
        }),
        finalize(() => this.settingsLoading.set(false))
      )
      .subscribe({
        next: credential => {
          this.apiCredentials.set([credential]);
          this.setCredentialState(credential.appId, { appKey, isActionApplied: false });
          this.notificationService.success('API settings enabled', 'API settings');
        },
        error: e => {
          this.settingsEnabled.set(false);
          this.notificationService.error({
            title: 'API settings',
            message: 'Failed to initialize API settings',
            serviceName: 'GATEWAY',
            requestMessage: e.statusText,
            requestCode: e.status,
          });
        },
      });
  }

  onSubmit() {
    this.savingSettings.set(true);
    const allowedIps = this.allowedIps();

    this.service
      .updateApiSettings({
        enabled: this.settingsEnabled(),
        allowedIps: allowedIps.length ? allowedIps.join(' ') : null,
        userId: this.currentUser()!.id,
        updatedAt: this.apiSettings()?.updatedAt ?? null,
      })
      .pipe(finalize(() => this.savingSettings.set(false)))
      .subscribe({
        next: res => {
          this.apiSettings.set(res);
          this.allowedIps.set(this.parseAllowedIps(res.allowedIps));
          this.notificationService.success('API settings updated', 'API settings');
        },
        error: e => {
          this.notificationService.error({
            title: 'Api settings',
            message: 'Update an error',
            serviceName: 'GATEWAY',
            requestMessage: e.statusText,
            requestCode: e.status,
          });
        },
      });
  }

  onChangeEnabled(enabled: boolean) {
    if (this.settingsLoading()) return;

    this.settingsEnabled.set(enabled);

    if (!enabled) {
      return;
    }

    if (!this.hasSettings()) {
      this.settingsLoading.set(true);
      this.populateInitialSettingsAndCredentials();
      return;
    }
  }

  randomString(m: number) {
    let s = '';
    const r = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_-=+-/';
    for (let i = 0; i < m; i++) {
      s += r.charAt(Math.floor(Math.random() * r.length));
    }
    return s;
  }

  addIpToList(): void {
    const ipFormControl: FormControl = this.form.controls.ip as FormControl;
    const ipValue = ipFormControl.value?.trim();
    const allowedIps = this.allowedIps();

    if (this.form.valid && ipValue.length > 0 && !allowedIps.includes(ipValue)) {
      this.allowedIps.set([...allowedIps, ipValue]);
      this.form.get('ip')?.patchValue('');
    }
  }

  removeIpFromList(ip: string): void {
    const allowedIps = this.allowedIps();
    this.allowedIps.set(allowedIps.filter(ai => ai !== ip));
  }

  async onClickCopy(textToCopy: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(textToCopy);
      this.notificationService.success('Copied to clipboard', 'API settings');
    } catch {
      this.notificationService.error({
        title: 'API settings',
        message: 'Fail to copy to clipboard. Please copy it manually.',
        serviceName: 'GATEWAY',
      });
    }
  }

  onSubmitLnt() {
    this.loading.set(true);
    if (this.callbackUrlLiveTesting && !this.callbackUrlLiveTesting.startsWith('http')) {
      this.callbackUrlLiveTesting = `https://${this.callbackUrlLiveTesting}`;
      this.formLnt.controls.callbackUrlLiveTesting.setValue(this.callbackUrlLiveTesting);
    }
    if (this.generalCallbackUrlLiveTesting && !this.generalCallbackUrlLiveTesting.startsWith('http')) {
      this.generalCallbackUrlLiveTesting = `https://${this.generalCallbackUrlLiveTesting}`;
      this.formLnt.controls.generalCallbackUrlLiveTesting.setValue(this.generalCallbackUrlLiveTesting);
    }
    const requests = [this.userService.updateCallbackUrlLiveTesting(this.callbackUrlLiveTesting)];
    if (this.isMain()) {
      requests.push(this.userService.updateGeneralUpdateCallbackUrl(this.generalCallbackUrlLiveTesting));
    }
    forkJoin(requests)
      .pipe(
        catchError(e => {
          this.loading.set(false);
          this.notificationService.error({
            title: 'API settings',
            message: 'Update an error',
            serviceName: 'GATEWAY',
            requestMessage: e.statusText,
            requestCode: e.status,
          });
          return throwError(() => e);
        })
      )
      .subscribe(() => {
        this.notificationService.success('Callback url updated', 'API settings');
        this.loading.set(false);
      });
  }

  private loadCredentials(): void {
    this.settingsLoading.set(true);

    this.service
      .listCredentials()
      .pipe(finalize(() => this.settingsLoading.set(false)))
      .subscribe({
        next: credentials => {
          this.apiCredentials.set(credentials);
        },
        error: e => {
          this.notificationService.error({
            title: 'API settings',
            message: 'Failed to load api credentials',
            serviceName: 'GATEWAY',
            requestMessage: e.statusText,
            requestCode: e.status,
          });
        },
      });
  }

  createCredential() {
    if (!this.canCreateCredential()) {
      return;
    }

    const appKey = this.randomString(35);
    this.isCredentialCreating.set(true);
    this.service
      .createCredential({ appKey })
      .pipe(finalize(() => this.isCredentialCreating.set(false)))
      .subscribe({
        next: credential => {
          this.setCredentialState(credential.appId, { appKey, isActionApplied: false });
          this.apiCredentials.set([...this.apiCredentials(), credential]);
          this.notificationService.success('Credential created', 'API settings');
        },
        error: e => {
          this.notificationService.error({
            title: 'API settings',
            message: 'Create credential error',
            serviceName: 'GATEWAY',
            requestMessage: e.statusText,
            requestCode: e.status,
          });
        },
      });
  }

  onClickRegenerate(credential: ApiCredentialDto) {
    const regenerateModalDialogRef = this.modal.alert().component(this.regenerateModal).dialogClass('modal-dialog small-modal').open();
    regenerateModalDialogRef.result.then(result => {
      if (result) {
        this.regenerateCredential(credential);
      }
    });
  }

  onClickDelete(credential: ApiCredentialDto) {
    const deleteModalDialogRef = this.modal.alert().component(this.deleteCredentialModal).dialogClass('modal-dialog small-modal').open();
    deleteModalDialogRef.result.then(result => {
      if (result) {
        this.deleteCredential(credential);
      }
    });
  }

  private regenerateCredential(credential: ApiCredentialDto): void {
    const appKey = this.randomString(35);
    const previousAppKey = this.getCredentialState(credential.appId).appKey;
    this.setCredentialState(credential.appId, { isActionApplied: true });

    this.service.regenerateCredential(credential.appId, { appKey }).subscribe({
      next: () => {
        this.setCredentialState(credential.appId, { appKey, isActionApplied: false });
        this.notificationService.success('Credential updated', 'API settings');
      },
      error: e => {
        this.setCredentialState(credential.appId, { appKey: previousAppKey, isActionApplied: false });
        this.notificationService.error({
          title: 'API settings',
          message: 'Failed to regenerate credential',
          serviceName: 'GATEWAY',
          requestMessage: e.statusText,
          requestCode: e.status,
        });
      },
    });
  }

  private deleteCredential(credential: ApiCredentialDto): void {
    this.setCredentialState(credential.appId, { isActionApplied: true });
    this.service.deleteCredential(credential.appId).subscribe({
      next: () => {
        this.removeCredentialState(credential.appId);
        this.apiCredentials.set(this.apiCredentials().filter(item => item.appId !== credential.appId));
        this.notificationService.success('Credential deleted', 'API settings');
      },
      error: e => {
        this.setCredentialState(credential.appId, { isActionApplied: false });
        this.notificationService.error({
          title: 'API settings',
          message: 'Failed to delete credential',
          serviceName: 'GATEWAY',
          requestMessage: e.statusText,
          requestCode: e.status,
        });
      },
    });
  }

  getCredentialState(appId: number): ApiCredentialState {
    return this.credentialsState()[appId] ?? { appKey: '', isActionApplied: false };
  }

  private parseAllowedIps(allowedIps: string | null | undefined): string[] {
    if (!allowedIps) {
      return [];
    }

    return allowedIps
      .split(' ')
      .map(item => item.trim())
      .filter(item => item.length > 0);
  }

  private setCredentialState(appId: number, patch: Partial<ApiCredentialState>): void {
    this.credentialsState.update(state => ({
      ...state,
      [appId]: { ...this.getCredentialState(appId), ...patch },
    }));
  }

  private removeCredentialState(appId: number): void {
    this.credentialsState.update(state => {
      const { [appId]: _, ...rest } = state;
      return rest;
    });
  }
}
